import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress
} from '@mui/material'
import { Warning } from '@mui/icons-material'

interface SessionWarningProps {
  open: boolean
  onExtend: () => void
  onLogout: () => void
  warningMinutes?: number
}

const SessionWarning: React.FC<SessionWarningProps> = ({
  open,
  onExtend,
  onLogout,
  warningMinutes = 5
}) => {
  // FIX: Folosim warningMinutes direct în loc de getTimeUntilTimeout()
  // care crea un nou useSession cu default 15 min și cauza logout prematur
  const [timeRemaining, setTimeRemaining] = useState(warningMinutes * 60)
  const startTimeRef = useRef<number>(0)

  useEffect(() => {
    if (!open) {
      // Reset când se închide dialogul
      setTimeRemaining(warningMinutes * 60)
      return
    }

    // Salvează momentul când s-a deschis dialogul
    startTimeRef.current = Date.now()
    setTimeRemaining(warningMinutes * 60)

    const interval = setInterval(() => {
      // Calculează timpul rămas bazat pe timpul real trecut
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
      const remaining = Math.max(0, warningMinutes * 60 - elapsed)
      setTimeRemaining(remaining)

      if (remaining <= 0) {
        onLogout()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [open, warningMinutes, onLogout])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const progress = ((warningMinutes * 60 - timeRemaining) / (warningMinutes * 60)) * 100

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning color="warning" />
        Sesiunea va expira în curând
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Sesiunea dvs. va expira în{' '}
          <strong>{formatTime(timeRemaining)}</strong>.
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pentru a continua să lucrați, apăsați butonul "Extinde sesiunea".
        </Typography>

        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            color="warning"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onLogout}
          color="error"
        >
          Deconectare
        </Button>
        <Button
          variant="contained"
          onClick={onExtend}
          autoFocus
        >
          Extinde sesiunea
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SessionWarning
